import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Grid,
    Image,
    Button,
    IconButton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    RangeSlider,
    RangeSliderTrack,
    RangeSliderFilledTrack,
    RangeSliderThumb
} from '@chakra-ui/react'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'

import {getUrl} from '../../../utils/cloudinary-utils'
import getCloudinaryVideoUrl from '../../cloudinary-media/video'

const Video = ({
    src,
    videoAlt,
    videoEnableSound,
    videoDisableAutoPlay,
    deferUntilVisible = false,
    width,
    variant,
    isModalOpen = false
}) => {
    const videoRef = useRef(null)
    const hasBeenVisible = useIntersectionObserver(deferUntilVisible ? videoRef : null, {
        useOnce: true
    })
    const shouldDisableAutoPlay = videoDisableAutoPlay || (deferUntilVisible && !hasBeenVisible)
    const [isPlaying, setIsPlaying] = useState(!shouldDisableAutoPlay)
    const [isMuted, setIsMuted] = useState(true)
    const [soundControlEnabled, setSoundControlEnabled] = useState(false)
    const [volume, setVolume] = useState(0)
    const DEFAULT_VOLUME = 0
    const timeToClose = 5000
    let timer

    const videoSrc = getCloudinaryVideoUrl({src, width: width, aspectRatio: '469:587'})

    useEffect(() => {
        if (!videoRef.current) return

        if (shouldDisableAutoPlay) {
            videoRef.current.pause()
            setIsPlaying(false)
            return
        }

        setIsPlaying(true)
        videoRef.current.play()?.catch(() => setIsPlaying(false))
    }, [shouldDisableAutoPlay])

    const togglePlayPause = (videoRef) => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
            } else {
                videoRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }

    const toggleSoundControl = () => {
        if (videoRef.current) {
            const newValue = isMuted ? 100 : 0
            videoRef.current.muted = !isMuted
            videoRef.current.volume = newValue / 100
            setVolume(newValue)
            setIsMuted(!isMuted)
        }
    }

    const handleVolumeChange = (videoRef, volume) => {
        if (videoRef.current) {
            videoRef.current.volume = volume
            videoRef.current.muted = false
            setVolume(volume * 100)
            setIsMuted(false)
        }
    }

    const handleAutoSoundControlOpen = () => {
        if (timer) {
            clearTimeout(timer)
        }
        setSoundControlEnabled(true)
    }

    const handleAutoSoundControlClose = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(() => {
            setSoundControlEnabled(false)
        }, timeToClose)
    }

    return (
        <Grid
            width="100%"
            height="100%"
            sx={{
                video: {
                    aspectRatio: '469 / 587'
                }
            }}
        >
            <video
                ref={videoRef}
                src={videoSrc || src}
                poster={videoSrc ? `${videoSrc?.replace('mp4', '')}.webp` : undefined}
                {...(isPlaying && {autoPlay: true})}
                preload={shouldDisableAutoPlay ? 'none' : 'metadata'}
                muted
                alt={videoAlt}
                loop
                playsInline
                style={{
                    gridArea: '1 / 1',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                }}
            />

            <Button
                onClick={() => togglePlayPause(videoRef)}
                aria-label="Toggle Play/Pause"
                className="play-pause-btn"
                gridArea="1 / 1"
                display="flex"
                alignItems={{
                    base: variant === 'thumb' ? 'center' : 'start',
                    lg: 'center'
                }}
                justifyContent={{
                    base: variant === 'thumb' ? 'center' : 'start',
                    lg: 'center'
                }}
                padding={{
                    base: variant === 'thumb' ? 0 : '20px',
                    lg: 0
                }}
                variant="unstyled"
                height="auto"
                minHeight="auto"
                minWidth="auto"
                gap="10px"
                zIndex="1"
                transition="opacity 0.3s ease-in-out"
                opacity={{base: 1, lg: 0}}
                _hover={{
                    opacity: {lg: 1}
                }}
            >
                <Image
                    width={{base: '30px', lg: '100px'}}
                    height={{base: '30px', lg: '100px'}}
                    opacity="0.6"
                    src={
                        variant === 'thumb'
                            ? getUrl('UIImages/images/video-play-border.svg')
                            : isPlaying
                            ? getUrl('UIImages/images/video-pause-hover.svg')
                            : getUrl('UIImages/images/video-play-hover.svg')
                    }
                    alt="Pause/ Play Icon"
                    className="control-icon"
                    _hover={{
                        opacity: {lg: 1}
                    }}
                />
            </Button>

            <Flex
                gridArea="1 / 1"
                alignItems="start"
                justifyContent="start"
                padding="20px"
                paddingLeft={{base: '60px', lg: '20px'}}
                gap="10px"
            >
                {videoEnableSound && (
                    <>
                        <IconButton
                            onClick={toggleSoundControl}
                            onMouseOver={handleAutoSoundControlOpen}
                            onMouseLeave={handleAutoSoundControlClose}
                            onTouchStart={handleAutoSoundControlOpen}
                            onTouchEnd={handleAutoSoundControlClose}
                            aria-label="Toggle Mute/Unmute"
                            icon={
                                <Image
                                    width="30px"
                                    height="30px"
                                    className="control-icon"
                                    src={
                                        isMuted
                                            ? getUrl('UIImages/images/mute-hover.svg')
                                            : volume > 50
                                            ? getUrl('UIImages/images/unmute-hover.svg')
                                            : volume === 0
                                            ? getUrl('UIImages/images/mute-hover.svg')
                                            : getUrl('UIImages/images/unmute-hover.svg')
                                    }
                                    alt="Mute/ Unmute Icon"
                                />
                            }
                            variant="unstyled"
                            height="auto"
                            minHeight="auto"
                            minWidth="auto"
                            gap="10px"
                            display="inline-flex"
                            zIndex="1"
                            _hover={{
                                opacity: '1'
                            }}
                        />

                        <Box
                            position="relative"
                            zIndex="1"
                            width="120px"
                            display={isModalOpen ? 'none' : 'block'}
                            paddingTop="3px"
                            paddingBottom="4px"
                            paddingX="25px"
                            borderRadius="3px"
                            background="rgba(0, 0, 0, 0.6)"
                            transition="opacity 0.3s ease-in-out"
                            opacity={soundControlEnabled ? 1 : 0}
                        >
                            <RangeSlider
                                aria-label="Volume Control"
                                defaultValue={[DEFAULT_VOLUME]}
                                value={[volume]}
                                onChange={(e) => handleVolumeChange(videoRef, e[0] / 100)}
                                onMouseOver={handleAutoSoundControlOpen}
                                onMouseLeave={handleAutoSoundControlClose}
                            >
                                <RangeSliderTrack
                                    sx={{
                                        '--slider-track-size': '2px',
                                        '--slider-bg': 'rgba(255, 255, 255, 0.5)'
                                    }}
                                >
                                    <RangeSliderFilledTrack
                                        sx={{
                                            '--slider-bg': 'transparent'
                                        }}
                                    />
                                </RangeSliderTrack>
                                <RangeSliderThumb
                                    index={0}
                                    sx={{
                                        '--slider-thumb-size': '10px'
                                    }}
                                />
                            </RangeSlider>
                        </Box>
                    </>
                )}
            </Flex>
        </Grid>
    )
}

Video.propTypes = {
    src: PropTypes.string,
    videoAlt: PropTypes.string,
    isModalOpen: PropTypes.bool,
    videoEnableSound: PropTypes.bool,
    videoDisableAutoPlay: PropTypes.bool,
    deferUntilVisible: PropTypes.bool,
    width: PropTypes.number,
    variant: PropTypes.string
}

export default Video
